import React, { useState } from "react";

import { useIntl } from "react-intl";

import InternalTag from "Components/InternalTag";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import TimeframeControl from "src/organization/pages/project/environment/metrics/components/timeframeControl/TimeframeControl";
import useActivityToggle from "src/organization/pages/project/environment/metrics/hooks/useActivityToggle";
import useInitialTimeframe from "src/organization/pages/project/environment/metrics/hooks/useInitialTimeframe";
import { getPollInterval } from "src/organization/pages/project/environment/metrics/utils/polling/utils";
import { useAppSelector } from "Store/hooks";

import { MetricsContext } from "../hooks/useMetricsContext";
import usePinnedTabsSync from "../hooks/usePinnedTabsSync";
import useStickyTimecontrol from "../hooks/useStickyTimecontrol";
import TrafficInfo from "../http/components/trafficInfo/TrafficInfo";

import { HttpBandWidth } from "./bandwidth";
import useHttpMetricsPoll from "./hook/useHttpMetricsPoll";
import TopUrlView from "./impact/timerangeChart";
import PageSkeleton from "./PageSkeleton";
import { HttpRequest } from "./request";
import * as S from "./style";

const Page = () => {
  const intl = useIntl();

  const usageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);

  const [timeFrame, setTimeFrame] = useState(useInitialTimeframe());

  const [isDeployment, setDeployment] = useActivityToggle();
  const pollInterval = getPollInterval(timeFrame);

  const { data, status, lastUpdated } = useHttpMetricsPoll({
    timeFrame
  });

  const { onSyncChange, isSynced, canJoinSync } = usePinnedTabsSync({
    timeFrame: timeFrame,
    setTimeFrame: setTimeFrame
  });

  const handleSyncChange = (isSynced: boolean) => {
    onSyncChange(isSynced, timeFrame);
  };
  const stickyRef = useStickyTimecontrol();

  return (
    <MetricsContext.Provider
      value={{ data, timeFrame, setTimeFrame, isDeployment }}
    >
      <div>
        <S.HeaderSection ref={stickyRef}>
          <S.PageTitle tag="h3">
            {intl.formatMessage({ id: "http_traffic2" })}
            {!usageBilling && <InternalTag />}
          </S.PageTitle>

          <S.ChartFilterContainer>
            <TimeframeControl
              isUpdating={status.isLoading}
              pollInterval={pollInterval}
              lastUpdated={lastUpdated}
              deployment={{
                enabled: isDeployment,
                onChange: setDeployment
              }}
              timeframe={{
                timeframe: timeFrame,
                onChange: setTimeFrame
              }}
              tabSync={{
                onSyncChange: handleSyncChange,
                isSynced,
                canJoinSync
              }}
            />
          </S.ChartFilterContainer>
        </S.HeaderSection>
        <PageSkeleton isLoading={status.refresh}>
          <TrafficInfo />
          <S.RequestBandWidthContainer>
            <S.RequestWrapper>
              <HttpRequest data={data} />
            </S.RequestWrapper>
            <hr className="divider" />
            <S.BandWidthWrapper>
              <HttpBandWidth data={data} />
            </S.BandWidthWrapper>
          </S.RequestBandWidthContainer>

          <TopUrlView data={data} />
        </PageSkeleton>
      </div>
    </MetricsContext.Provider>
  );
};

export default Page;
